import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import SEOHead from "@/components/SEOHead";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { AppHeader } from "@/components/AppHeader";
import LandingNav from "@/components/LandingNav";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { Bot, Plug, Terminal, Search, List, FolderSearch, FileText, TreePine, FilePlus, FolderPlus, FileEdit, Trash2 } from "lucide-react";

const MCP_ENDPOINT = "https://api.testassets.io/v1/mcp-server";

const McpDocs = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <SidebarProvider defaultOpen={false}>
      <div className={`min-h-screen flex ${user ? "flex-row" : "flex-col"} bg-background w-full`}>
        <SEOHead
          title="MCP Server Documentation – TestAssets"
          description="Connect AI agents like Claude Desktop, Cursor, and Windsurf to your TestAssets data using the Model Context Protocol (MCP) server."
          path="/mcp-docs"
          breadcrumbs={[
            { name: "Home", url: "/" },
            { name: "MCP Documentation", url: "/mcp-docs" },
          ]}
        />
        {user ? (
          <>
            <AppSidebar />
            <div className="flex-1 flex flex-col min-w-0">
              <AppHeader />
              <McpDocsContent />
              <Footer />
            </div>
          </>
        ) : (
          <>
            <LandingNav />
            <McpDocsContent />
            <Footer />
          </>
        )}
      </div>
    </SidebarProvider>
  );
};

const McpDocsContent = () => {
  const { t } = useTranslation();

  const tools = [
    {
      name: "list_asset_lists",
      icon: List,
      description: t("mcpDocs.tools.listAssetLists", "List all asset lists you own or have access to, along with your role (owner, admin, editor, viewer)."),
    },
    {
      name: "get_assets",
      icon: FolderSearch,
      description: t("mcpDocs.tools.getAssets", "Retrieve all assets in a specific list. Supports an optional search filter that matches on both name and notes."),
    },
    {
      name: "search_assets",
      icon: Search,
      description: t("mcpDocs.tools.searchAssets", "Search assets by name or notes across all accessible asset lists. Returns up to 100 results."),
    },
    {
      name: "get_asset_detail",
      icon: FileText,
      description: t("mcpDocs.tools.getAssetDetail", "Get a single asset with full context: breadcrumb path (parent chain), direct children, and sibling count — ideal for AI agents that need to understand where an asset sits in the hierarchy."),
    },
    {
      name: "get_asset_tree",
      icon: TreePine,
      description: t("mcpDocs.tools.getAssetTree", "Return an entire asset list (or a subtree starting from a given asset) as a nested JSON hierarchy. Capped at 1,000 items per request."),
    },
    {
      name: "create_asset_list",
      icon: FolderPlus,
      description: t("mcpDocs.tools.createAssetList", "Create a new asset list. The API key user becomes the owner. Requires a title and optional description."),
    },
    {
      name: "create_asset",
      icon: FilePlus,
      description: t("mcpDocs.tools.createAsset", "Create a new asset in a list, optionally nested under a parent. Requires Owner, Admin, or Editor role. Names are limited to 200 characters. Supports optional is_qr_code and is_dmc_code flags to render the asset as a QR or Data Matrix (DMC) code on the asset details page."),
    },
    {
      name: "update_asset",
      icon: FileEdit,
      description: t("mcpDocs.tools.updateAsset", "Update an asset's name, comment, parent, or barcode flags (is_qr_code, is_dmc_code). Requires Owner, Admin, or Editor role. Self-parenting is prevented."),
    },
    {
      name: "delete_asset",
      icon: Trash2,
      description: t("mcpDocs.tools.deleteAsset", "Delete an asset (and its children, via cascade). Requires Owner or Admin role."),
    },
  ];

  return (
    <main className="flex-1">
      <section className="container mx-auto px-6 py-16 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Bot className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold tracking-tight text-foreground">
              {t("mcpDocs.title", "MCP Server")}
            </h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("mcpDocs.description", "Connect AI agents like Claude Desktop, Cursor, and Windsurf to your TestAssets data using the Model Context Protocol (MCP).")}
          </p>
        </div>

        {/* What is MCP */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Plug className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>{t("mcpDocs.whatIsMcp.title", "What is MCP?")}</CardTitle>
                <CardDescription>{t("mcpDocs.whatIsMcp.subtitle", "Model Context Protocol")}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              {t("mcpDocs.whatIsMcp.body", "The Model Context Protocol (MCP) is an open standard that allows AI assistants to connect to external data sources and tools. By connecting your TestAssets account via MCP, AI agents can read your asset lists and search your test data — enabling workflows like generating test plans, reviewing coverage, or finding specific test assets by name or notes.")}
            </p>
          </CardContent>
        </Card>

        {/* Prerequisites */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t("mcpDocs.prerequisites.title", "Prerequisites")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <ol className="list-decimal list-inside space-y-2">
              <li>
                {t("mcpDocs.prerequisites.account", "A TestAssets account with at least one asset list.")}
              </li>
              <li>
                {t("mcpDocs.prerequisites.apiKey", "An API key — generate one from")}
                {" "}
                <Link to="/app/settings" className="text-primary hover:underline font-medium">
                  {t("mcpDocs.prerequisites.settingsLink", "Settings → API Keys")}
                </Link>.
              </li>
              <li>
                {t("mcpDocs.prerequisites.client", "An MCP-compatible client (Claude Desktop, Cursor, Windsurf, etc.).")}
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Available Tools */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Terminal className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>{t("mcpDocs.availableTools.title", "Available Tools")}</CardTitle>
                <CardDescription>{t("mcpDocs.availableTools.subtitle", "Nine tools are exposed by the MCP server, covering read, hierarchy, and write operations")}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {tools.map((tool) => (
              <div key={tool.name} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border border-border">
                <tool.icon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <code className="text-sm font-semibold text-foreground">{tool.name}</code>
                  <p className="text-sm text-muted-foreground mt-1">{tool.description}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Configuration Examples */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t("mcpDocs.configuration.title", "Configuration")}</CardTitle>
            <CardDescription>{t("mcpDocs.configuration.subtitle", "Add the following to your MCP client configuration")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Claude Desktop */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-semibold text-foreground">Claude Desktop</h3>
                <Badge variant="secondary" className="text-xs">claude_desktop_config.json</Badge>
              </div>
              <pre className="bg-muted rounded-lg p-4 text-xs overflow-x-auto border border-border">
                <code className="text-foreground">{JSON.stringify({
                  mcpServers: {
                    testassets: {
                      url: MCP_ENDPOINT,
                      headers: {
                        apikey: "YOUR_API_KEY"
                      }
                    }
                  }
                }, null, 2)}</code>
              </pre>
            </div>

            {/* Cursor */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-semibold text-foreground">Cursor</h3>
                <Badge variant="secondary" className="text-xs">.cursor/mcp.json</Badge>
              </div>
              <pre className="bg-muted rounded-lg p-4 text-xs overflow-x-auto border border-border">
                <code className="text-foreground">{JSON.stringify({
                  mcpServers: {
                    testassets: {
                      url: MCP_ENDPOINT,
                      headers: {
                        apikey: "YOUR_API_KEY"
                      }
                    }
                  }
                }, null, 2)}</code>
              </pre>
            </div>

            {/* Windsurf */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-semibold text-foreground">Windsurf</h3>
                <Badge variant="secondary" className="text-xs">~/.codeium/windsurf/mcp_config.json</Badge>
              </div>
              <pre className="bg-muted rounded-lg p-4 text-xs overflow-x-auto border border-border">
                <code className="text-foreground">{JSON.stringify({
                  mcpServers: {
                    testassets: {
                      serverUrl: MCP_ENDPOINT,
                      headers: {
                        apikey: "YOUR_API_KEY"
                      }
                    }
                  }
                }, null, 2)}</code>
              </pre>
            </div>

            {/* VS Code */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-semibold text-foreground">VS Code</h3>
                <Badge variant="secondary" className="text-xs">.vscode/mcp.json</Badge>
              </div>
              <pre className="bg-muted rounded-lg p-4 text-xs overflow-x-auto border border-border">
                <code className="text-foreground">{JSON.stringify({
                  servers: {
                    testassets: {
                      type: "http",
                      url: MCP_ENDPOINT,
                      headers: {
                        Authorization: "Bearer YOUR_API_KEY"
                      }
                    }
                  }
                }, null, 2)}</code>
              </pre>
              <p className="text-xs text-muted-foreground mt-2">
                {t("mcpDocs.configuration.vsCodeNote", "Two options: (1) Provide a static API key via the Authorization header as shown above — fastest setup. (2) Omit the headers block and VS Code will open a browser-based OAuth flow where you paste your API key once; the issued token is reused automatically afterwards.")}
              </p>
            </div>

            {/* GitHub Copilot CLI */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-semibold text-foreground">GitHub Copilot CLI</h3>
                <Badge variant="secondary" className="text-xs">~/.copilot/mcp-config.json</Badge>
              </div>
              <pre className="bg-muted rounded-lg p-4 text-xs overflow-x-auto border border-border">
                <code className="text-foreground">{JSON.stringify({
                  mcpServers: {
                    testassets: {
                      type: "http",
                      url: MCP_ENDPOINT,
                      headers: {
                        Authorization: "Bearer YOUR_API_KEY"
                      }
                    }
                  }
                }, null, 2)}</code>
              </pre>
              <p className="text-xs text-muted-foreground mt-2">
                {t("mcpDocs.configuration.copilotCliNote", "Two ways to add the server: (1) Run `copilot mcp add testassets --transport http https://api.testassets.io/v1/mcp-server --header \"Authorization: Bearer YOUR_API_KEY\"` in your terminal, or (2) inside an interactive Copilot session, type `/mcp add`, choose HTTP, and paste the URL plus the Authorization header. Edit the config file at ~/.copilot/mcp-config.json to make the change permanent.")}
              </p>
            </div>


            <p className="text-sm text-muted-foreground">
              {t("mcpDocs.configuration.replaceKey", "Replace YOUR_API_KEY with your actual API key from")}
              {" "}
              <Link to="/app/settings" className="text-primary hover:underline font-medium">
                {t("mcpDocs.configuration.settingsLink", "Settings → API Keys")}
              </Link>.
            </p>
          </CardContent>
        </Card>

        {/* Endpoint Details */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t("mcpDocs.endpoint.title", "Endpoint Details")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-500 text-white">URL</Badge>
              <code className="text-xs text-foreground break-all">{MCP_ENDPOINT}</code>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-500 text-white">Protocol</Badge>
              <span className="text-muted-foreground">MCP Streamable HTTP</span>
            </div>
            <div className="flex items-start gap-2">
              <Badge className="bg-yellow-500 text-white mt-0.5">Auth</Badge>
              <span className="text-muted-foreground">
                {t("mcpDocs.endpoint.authBoth", "API key via either the 'apikey' header or 'Authorization: Bearer <token>'. Use the same key issued for the REST API.")}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Cross-links */}
        <div className="mt-12 flex flex-wrap justify-center gap-4 text-sm">
          <Link to="/api-docs" className="text-primary hover:underline">
            {t("mcpDocs.links.apiDocs", "REST API Documentation")} →
          </Link>
          <Link to="/applications" className="text-primary hover:underline">
            {t("mcpDocs.links.applications", "Applications & Integrations")} →
          </Link>
          <Link to="/help" className="text-primary hover:underline">
            {t("mcpDocs.links.help", "Help Center")} →
          </Link>
        </div>
      </section>
    </main>
  );
};

export default McpDocs;
